// ФАЙЛ: ZoneLayoutManager.tsx
// НАЗНАЧЕНИЕ: Слой компоновки overlay. Рендерит 8 фиксированных зон через
// CSS Grid на весь экран, предоставляет ZoneLayoutProvider дочерним слоям и
// оборачивает их в Error Boundary (падение одного плагина не роняет overlay).
// Учитывает ограничения OBS CEF: только базовый Grid/Flexbox, явная
// прозрачность фона, pointer-events сквозные для прозрачных участков.

// @ts-ignore — React берётся из окружения overlay-приложения.
import { Component } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import {
  ZoneLayoutProvider,
  useZoneLayoutContext,
} from '@shared/lib/zone-layout/ZoneLayoutContext'
import { ZONE_IDS, type ZoneId } from '@shared/lib/zone-layout/types'

// z-index слоя зон: выше GamesLayer (1), ниже отладочных индикаторов.
const ZONE_LAYER_Z_INDEX = 5
// Отступ содержимого зон от краёв экрана и между виджетами в стеке.
const ZONE_EDGE_PADDING = '16px'
const ZONE_STACK_GAP = '8px'

// Раскладка зон по ячейкам CSS Grid (middle-center намеренно пустой).
const GRID_TEMPLATE_AREAS = [
  '"tl tc tr"',
  '"ml mc mr"',
  '"bl bc br"',
].join(' ')

const ZONE_GRID_AREA: Record<ZoneId, string> = {
  'top-left': 'tl',
  'top-center': 'tc',
  'top-right': 'tr',
  'middle-left': 'ml',
  'middle-right': 'mr',
  'bottom-left': 'bl',
  'bottom-center': 'bc',
  'bottom-right': 'br',
}

/** Вычисляет flex-стиль зоны по её вертикальному и горизонтальному якорю. */
function zoneFlexStyle(zone: ZoneId): CSSProperties {
  const isBottom = zone.startsWith('bottom-')
  const isMiddle = zone.startsWith('middle-')
  const horizontal = zone.endsWith('-left')
    ? 'flex-start'
    : zone.endsWith('-right')
      ? 'flex-end'
      : 'center'

  return {
    gridArea: ZONE_GRID_AREA[zone],
    display: 'flex',
    flexDirection: isBottom ? 'column-reverse' : 'column',
    justifyContent: isMiddle ? 'center' : 'flex-start',
    alignItems: horizontal,
    gap: ZONE_STACK_GAP,
    padding: ZONE_EDGE_PADDING,
    boxSizing: 'border-box',
    overflow: 'hidden',
    // Клики проходят сквозь пустые участки зоны к стриму под overlay.
    pointerEvents: 'none',
  }
}

// Сетка зон: рендерит 8 контейнеров и связывает их DOM-узлы с контекстом.
function ZoneGrid() {
  const ctx = useZoneLayoutContext()
  if (!ctx) return null

  const gridStyle: CSSProperties = {
    position: 'fixed',
    inset: 0,
    width: '100%',
    height: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gridTemplateRows: '1fr 1fr 1fr',
    gridTemplateAreas: GRID_TEMPLATE_AREAS,
    background: 'transparent',
    pointerEvents: 'none',
    zIndex: ZONE_LAYER_Z_INDEX,
  }

  return (
    <div style={gridStyle} aria-hidden="true" data-testid="zone-layout-grid">
      {ZONE_IDS.map((zone) => (
        <div
          key={zone}
          data-zone={zone}
          style={zoneFlexStyle(zone)}
          ref={(node) => ctx.setZoneNode(zone, node)}
        />
      ))}
    </div>
  )
}

interface ErrorBoundaryProps {
  children: ReactNode
}
interface ErrorBoundaryState {
  hasError: boolean
}

// Error Boundary: изолирует падения зонного слоя от остального overlay.
class ZoneErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidCatch(error: unknown) {
    console.error('[zone-layout] сбой в слое зон:', error)
  }

  render() {
    if (this.state.hasError) return null
    return this.props.children
  }
}

/**
 * Корневой компонент слоя компоновки. Оборачивает дочерние слои (EffectsLayer)
 * в провайдер зон и Error Boundary, поверх рендерит сетку зон.
 */
export function ZoneLayoutManager({ children }: { children: ReactNode }) {
  return (
    <ZoneErrorBoundary>
      <ZoneLayoutProvider>
        <ZoneGrid />
        {children}
      </ZoneLayoutProvider>
    </ZoneErrorBoundary>
  )
}
